import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Radio, BadgeCheck, ShieldCheck, Download, Share2, Twitter, Linkedin, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { T, SOURCE_COLORS } from "@/lib/theme";

const REAL_METHODS = {
  icy_inband:   "ICY / RT+ in-band metadata",
  icecast:      "Icecast admin endpoint",
  shoutcast_v1: "Shoutcast statistics endpoint",
};

/** Inject / update <head> tags for SEO + OpenGraph + Twitter cards. Called
 *  once per cert load. Removes prior WSP-managed tags before re-injecting so
 *  navigating between certs doesn't leak stale metadata. */
function applyCertSEO(cert, certNumber) {
  if (typeof document === "undefined" || !cert) return;
  // Titles + descriptions.
  const isFp = cert.issuance_method === "fingerprint";
  const isManual = cert.issuance_method === "manual";
  // F-101: three-way branch on ALL SEO/OG surfaces (page title, description,
  // JSON-LD alternateName). Manual attestations must not carry independent-
  // verification vocabulary in cached third-party surfaces.
  const title = isManual
    ? `${cert.ad_title || "Radio ad"} on ${cert.station_name || "radio"} — WaveScorePro manual attestation`
    : `${cert.ad_title || "Radio ad"} on ${cert.station_name || "radio"} — WaveScorePro verified${isFp ? " with audio fingerprint" : ""}`;
  const desc = isManual
    ? `Manually attested broadcast record — not independently verified. Reported reach at airtime: ${(cert.estimated_total_reach || 0).toLocaleString()} listeners.`
    : `Independently verified radio-ad broadcast. Reach at airtime: ${(cert.estimated_total_reach || 0).toLocaleString()} listeners. ${isFp ? "Client-side re-hashable audio evidence." : "Direct broadcast-stream capture."}`;
  // Absolute badge URL — prefer the configured API base, fall back to the
  // current origin so misconfigured deployments still emit valid OG images.
  const base = (api.defaults.baseURL || window.location.origin).replace(/\/+$/, "");
  const badgeUrl = `${base}/verify/${certNumber}/badge.svg`;
  const canonical = `${window.location.origin}/verify/${certNumber}`;

  document.title = title;

  // Idempotent tag setter — reuses existing element by [name] or [property]
  // so we never accumulate duplicates across route changes.
  const setMeta = (attr, value, content) => {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${value}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, value);
      el.setAttribute("data-wsp-managed", "1");
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  const setLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      el.setAttribute("data-wsp-managed", "1");
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  };

  setMeta("name", "description", desc);
  // OpenGraph — Facebook, LinkedIn, Slack, iMessage previews.
  setMeta("property", "og:type", "article");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:image", badgeUrl);
  setMeta("property", "og:image:width", "600");
  setMeta("property", "og:image:height", "200");
  setMeta("property", "og:url", canonical);
  setMeta("property", "og:site_name", "WaveScorePro");
  // Twitter / X cards.
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", desc);
  setMeta("name", "twitter:image", badgeUrl);
  // Canonical URL — critical for SEO with hash-routing frontends.
  setLink("canonical", canonical);

  // JSON-LD structured data — helps Google understand the page is a
  // verifiable claim about a broadcast event.
  const existing = document.head.querySelector('script[type="application/ld+json"][data-wsp-managed="1"]');
  if (existing) existing.remove();
  const ld = {
    "@context": "https://schema.org",
    "@type": "ClaimReview",
    "datePublished": cert.issued_at,
    "url": canonical,
    "claimReviewed": `${cert.ad_title} aired on ${cert.station_name} at ${(cert.detected_at || "").slice(0, 19)}`,
    "reviewRating": {
      "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1",
      "alternateName": isFp ? "Verified with audio fingerprint"
                        : isManual ? "Manually attested record — not independently verified"
                        : "Verified with broadcast-stream capture",
    },
    "author": { "@type": "Organization", "name": "WaveScorePro",
                "url": "https://www.wavescorepro.com" },
    "itemReviewed": {
      "@type": "CreativeWork", "name": cert.ad_title,
      "creator": cert.client_name ? { "@type": "Organization", "name": cert.client_name } : undefined,
      "publisher": { "@type": "RadioStation", "name": cert.station_name },
      "datePublished": cert.detected_at,
    },
  };
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-wsp-managed", "1");
  script.textContent = JSON.stringify(ld);
  document.head.appendChild(script);
}

/** Downloads the evidence WAV and re-hashes it in-browser via SubtleCrypto.
 *  Only invoked for fingerprint-issued certs (see gate below).
 *  Returns {status:'match'|'mismatch'|'error', hash, bytes, elapsed_ms}. */
async function rehashEvidence(certNumber) {
  const t0 = performance.now();
  const url = `${api.defaults.baseURL}/verify/${certNumber}/evidence`;
  const res = await fetch(url, { method: "GET" });
  if (!res.ok) {
    return { status: "error", detail: `HTTP ${res.status}` };
  }
  const expected = (res.headers.get("X-Evidence-SHA256") || "").toLowerCase();
  const buf = await res.arrayBuffer();
  const hashBuf = await crypto.subtle.digest("SHA-256", buf);
  const hex = Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0")).join("");
  const elapsed_ms = Math.round(performance.now() - t0);
  return {
    status: hex === expected ? "match" : "mismatch",
    hash: hex,
    expected,
    bytes: buf.byteLength,
    elapsed_ms,
    // Keep the raw bytes so a "Download" button can re-serve them without
    // a second network hit.
    _buf: buf,
    _headers: {
      ad_title: res.headers.get("X-Ad-Title"),
      station_name: res.headers.get("X-Station-Name"),
      detected_at: res.headers.get("X-Detected-At"),
      match_score: res.headers.get("X-Match-Score"),
      match_hits: res.headers.get("X-Match-Hits"),
    },
  };
}

export default function VerifyCertificate() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Evidence-verify state — only used for fingerprint-issued certs.
  const [ev, setEv] = useState({ status: "idle" });
  const runVerify = async () => {
    setEv({ status: "running" });
    try {
      const r = await rehashEvidence(id);
      setEv(r);
    } catch (e) {
      setEv({ status: "error", detail: e.message });
    }
  };
  const downloadEv = () => {
    if (!ev._buf) return;
    const blob = new Blob([ev._buf], { type: "audio/wav" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `wsp-verify-${id}.wav`; a.click();
    URL.revokeObjectURL(u);
  };

  useEffect(() => {
    api.get(`/verify/${id}`)
      .then((r) => {
        setCert(r.data);
        applyCertSEO(r.data, id);
      })
      .catch((e) => setError(e.response?.data?.detail || "Certificate not found or invalid"))
      .finally(() => setLoading(false));
    // Restore default title on unmount so other pages don't stay on this cert's SEO.
    return () => {
      document.title = "WaveScorePro — Know Your Wave";
      document.head
        .querySelectorAll('[data-wsp-managed="1"]')
        .forEach((el) => el.remove());
    };
  }, [id]);

  const shareLinks = {
    twitter: () => {
      const text = cert
        ? `${cert.ad_title || "Radio ad"} aired on ${cert.station_name} — verified by @wavescorepro (SHA-256 evidence)`
        : "";
      const url = `${window.location.origin}/verify/${id}`;
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    },
    linkedin: () => {
      const url = `${window.location.origin}/verify/${id}`;
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    },
    copy: async () => {
      const url = `${window.location.origin}/verify/${id}`;
      try { await navigator.clipboard.writeText(url); return true; } catch { return false; }
    },
  };
  const [copyOk, setCopyOk] = useState(false);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Inter',system-ui,sans-serif" }} data-testid="verify-page">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap')`}</style>
      <div style={{ background: T.navy, padding: "16px 48px", display: "flex", alignItems: "center", gap: 16 }}>
        <Link to="/" data-testid="verify-home" style={{ fontSize: 18, fontWeight: 800, color: "#fff", textDecoration: "none" }}>
          WaveScore<span style={{ color: T.green }}>Pro</span>
        </Link>
        <div style={{ color: "#94A3B8", fontSize: 14 }}>{t("verify.header")}</div>
      </div>

      <div style={{ maxWidth: 760, margin: "48px auto", padding: "0 24px" }}>
        {loading && <div style={{ textAlign: "center", padding: "80px", color: T.muted }} data-testid="verify-loading">Verifying certificate...</div>}
        {error && <div data-testid="verify-error" style={{ textAlign: "center", padding: "80px", color: "#EF4444", fontSize: 18 }}>⚠ {error}</div>}

        {cert && !loading && (
          <div data-testid="cert-card" style={{ background: "#fff", borderRadius: 16, padding: "40px", border: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: T.muted, marginBottom: 4, letterSpacing: "0.06em" }}>CERTIFICATE NUMBER</div>
                <div style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 16, color: T.navy }}>{cert.certificate_number}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{ background: SOURCE_COLORS[cert.confidence_label] || T.green, color: "#fff",
                  padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 800, fontFamily: "monospace" }}>
                  {cert.confidence_label || "DIRECT"}
                </span>
                <div style={{ color: T.green, fontWeight: 700, fontSize: 14, marginTop: 6 }}>✓ VERIFIED</div>
              </div>
            </div>

            {/* Proof-of-airplay receipt — only shown for real RT+ certs */}
            {REAL_METHODS[cert.metadata_method] && cert.raw_metadata && (
              <div data-testid="proof-of-airplay" style={{
                background: "linear-gradient(135deg,#ECFEFF 0%,#F0FDF4 100%)",
                border: `1px solid #99F6E4`, borderRadius: 12, padding: "18px 20px", marginBottom: 28,
                position: "relative", overflow: "hidden",
              }}>
                <div style={{ position: "absolute", top: -22, right: -22, width: 80, height: 80,
                  borderRadius: "50%", background: "radial-gradient(circle,#00C89622 0%,transparent 70%)" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <BadgeCheck size={18} color="#0E7490" strokeWidth={2.5} />
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#0E7490", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                    How was this verified?
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#134E4A", lineHeight: 1.6, marginBottom: 12 }}>
                  Read directly from the station&apos;s live broadcast stream via{" "}
                  <b>{REAL_METHODS[cert.metadata_method]}</b>. This is the same RT+ field
                  that an FM RDS receiver decodes — captured the moment the spot went on air.
                </div>
                <div data-testid="proof-of-airplay-rtplus" style={{ background: "#0A1628", color: "#7DD3FC",
                  padding: "12px 14px", borderRadius: 8, fontFamily: "'Space Mono','Courier New',monospace",
                  fontSize: 13, lineHeight: 1.5, display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <Radio size={16} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1, wordBreak: "break-word" }}>
                    <div style={{ color: "#94A3B8", fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>
                      StreamTitle =
                    </div>
                    <div style={{ color: "#A7F3D0", fontWeight: 700 }}>&quot;{cert.raw_metadata}&quot;</div>
                    <div style={{ color: "#94A3B8", fontSize: 11, marginTop: 6 }}>
                      heard at {cert.detected_at?.replace("T", " ").slice(0, 19)} UTC
                      {cert.station_name ? ` · ${cert.station_name}` : ""}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fingerprint evidence verification — public, no auth. */}
            {cert.issuance_method === "fingerprint" && (
              <div data-testid="fingerprint-evidence-block" style={{
                background: "linear-gradient(135deg,#EFF6FF 0%,#F5F3FF 100%)",
                border: `1px solid #C7D2FE`, borderRadius: 12, padding: "18px 20px", marginBottom: 28,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <ShieldCheck size={18} color="#4338CA" strokeWidth={2.5} />
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#4338CA", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                    How was this verified? Audio fingerprint
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#1E1B4B", lineHeight: 1.6, marginBottom: 12 }}>
                  We captured the audio at broadcast time and matched it against the
                  campaign&apos;s ad creative using our self-hosted landmark fingerprinter.
                  The SHA-256 below is the hash of that exact 10-second clip. Download
                  the audio and re-hash it in your browser — if the numbers match,
                  the certificate points at real audio that we cannot swap after-the-fact.
                </div>
                {cert.evidence_sha256 && (
                  <div style={{ background: "#0F172A", color: "#A5B4FC", padding: "10px 12px",
                    borderRadius: 6, fontFamily: "'Space Mono','Courier New',monospace",
                    fontSize: 11, wordBreak: "break-all", marginBottom: 12 }}
                    data-testid="cert-evidence-sha256">
                    <div style={{ color: "#64748B", fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>
                      Certificate SHA-256 (from cert PDF)
                    </div>
                    {cert.evidence_sha256}
                  </div>
                )}
                {ev.status === "idle" && (
                  <button
                    data-testid="verify-evidence-btn"
                    onClick={runVerify}
                    style={{ background: "#4338CA", color: "#fff", border: "none",
                      padding: "10px 18px", borderRadius: 6, fontWeight: 700, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <ShieldCheck size={16} /> Re-hash evidence in my browser
                  </button>
                )}
                {ev.status === "running" && (
                  <div data-testid="verify-evidence-running" style={{ color: "#4338CA", fontSize: 13, fontWeight: 600 }}>
                    Downloading & hashing…
                  </div>
                )}
                {ev.status === "match" && (
                  <div data-testid="verify-evidence-match">
                    <div style={{ display: "flex", alignItems: "center", gap: 10,
                      background: "#DCFCE7", border: "1px solid #86EFAC",
                      padding: "12px 14px", borderRadius: 6, marginBottom: 12 }}>
                      <BadgeCheck size={20} color="#166534" />
                      <div>
                        <div style={{ color: "#166534", fontWeight: 800, fontSize: 14 }}>
                          Hashes match — evidence is untampered.
                        </div>
                        <div style={{ color: "#166534", fontSize: 12, marginTop: 2 }}>
                          {ev.bytes.toLocaleString()} bytes hashed in {ev.elapsed_ms} ms.
                        </div>
                      </div>
                    </div>
                    <div style={{ background: "#0F172A", color: "#A7F3D0",
                      padding: "10px 12px", borderRadius: 6, fontFamily: "'Space Mono',monospace",
                      fontSize: 11, wordBreak: "break-all", marginBottom: 12 }}>
                      <div style={{ color: "#64748B", fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>
                        Your browser computed
                      </div>
                      {ev.hash}
                    </div>
                    <button data-testid="verify-evidence-download" onClick={downloadEv}
                      style={{ background: "#0F172A", color: "#fff", border: "none",
                        padding: "8px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                        display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Download size={14} /> Download the clip
                    </button>
                  </div>
                )}
                {ev.status === "mismatch" && (
                  <div data-testid="verify-evidence-mismatch" style={{ background: "#FEE2E2",
                    border: "1px solid #FCA5A5", padding: "12px 14px", borderRadius: 6 }}>
                    <div style={{ color: "#B91C1C", fontWeight: 800, fontSize: 14, marginBottom: 6 }}>
                      ⚠ Hash mismatch — do not trust this certificate.
                    </div>
                    <div style={{ color: "#7F1D1D", fontSize: 12, fontFamily: "monospace", wordBreak: "break-all" }}>
                      expected: {ev.expected}<br />
                      got:      {ev.hash}
                    </div>
                  </div>
                )}
                {ev.status === "error" && (
                  <div data-testid="verify-evidence-error" style={{ color: "#B91C1C", fontSize: 13, fontWeight: 600 }}>
                    Verification failed: {ev.detail || "unknown error"}
                  </div>
                )}
              </div>
            )}

            {/* Social share — viral distribution of verification */}
            <div data-testid="verify-share-block" style={{
              background: "#F8FAFC", border: `1px solid ${T.border}`, borderRadius: 10,
              padding: "14px 18px", marginBottom: 24,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Share2 size={18} color={T.navy} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>
                    {t("verify.share_title")}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    {t("verify.share_subtitle")}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <a
                  data-testid="share-twitter"
                  href={shareLinks.twitter()}
                  target="_blank" rel="noreferrer noopener"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6,
                    background: "#000", color: "#fff", padding: "8px 14px",
                    borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
                  <Twitter size={14} /> {t("verify.share_twitter")}
                </a>
                <a
                  data-testid="share-linkedin"
                  href={shareLinks.linkedin()}
                  target="_blank" rel="noreferrer noopener"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6,
                    background: "#0A66C2", color: "#fff", padding: "8px 14px",
                    borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
                  <Linkedin size={14} /> {t("verify.share_linkedin")}
                </a>
                <button
                  data-testid="share-copy"
                  onClick={async () => { const ok = await shareLinks.copy(); setCopyOk(ok); setTimeout(() => setCopyOk(false), 2000); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6,
                    background: copyOk ? "#DCFCE7" : "#F1F5F9",
                    color: copyOk ? "#166534" : T.navy,
                    border: `1px solid ${copyOk ? "#86EFAC" : T.border}`,
                    padding: "8px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  <Copy size={14} /> {copyOk ? t("verify.share_copied") : t("verify.share_copy")}
                </button>
              </div>
            </div>

            {[
              ["Station", cert.station_name || "—"],
              ["Ad Title", cert.ad_title || "—"],
              ["Client", cert.client_name || "—"],
              ["Agency", cert.agency_name || "—"],
              ["Scheduled", cert.scheduled_at?.slice(0, 19) || "—"],
              ["Detected at", cert.detected_at?.slice(0, 19) || "—"],
              ["Duration", cert.duration_seconds ? `${cert.duration_seconds} seconds` : "—"],
              ["Online connections at airtime", (cert.raw_connections_at_air || 0).toLocaleString()],
              ["Estimated individual listeners", (cert.estimated_people_at_air || 0).toLocaleString()],
              ["Estimated total reach", (cert.estimated_total_reach || 0).toLocaleString()],
              ["Data source", cert.data_source || "—"],
              ["Issued", cert.issued_at?.slice(0, 19) || "—"],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", borderBottom: `1px solid ${T.border}`, padding: "10px 0" }}>
                <div style={{ width: 240, color: T.muted, fontSize: 13, flexShrink: 0 }}>{label}</div>
                <div style={{ color: T.navy, fontSize: 13, fontWeight: 500 }}>{value}</div>
              </div>
            ))}

            <div style={{ marginTop: 24, padding: "14px 16px", background: "#F0FDF4", borderRadius: 8, border: `1px solid #BBF7D0` }}>
              <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.6 }}>
                This certificate was independently generated by WaveScorePro from live stream monitoring data.
                It was not produced by the station and cannot be altered by them. Data source: {cert.data_source || "DIRECT"}.
              </div>
            </div>
            <div style={{ textAlign: "center", marginTop: 24, color: T.muted, fontSize: 12 }}>
              wavescorepro.com · West Africa Audience Intelligence · Know Your Wave.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
